import { useState } from 'react'
import { command, recommend, useProject } from '../app/data'
import { useAppState } from '../app/state'
import type { Artifact, EquipmentRequest, Recommendation } from '../domain/models'
import { ActionForm, dateTime, localDateTime, toISO } from './forms'
import { ArtifactViewer, Badge, Modal, requestStatus } from './ui'

export function RequestForm({ onDone }: { onDone: () => void }) {
  const data = useProject()
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  tomorrow.setHours(8, 0, 0, 0)
  const end = new Date(tomorrow)
  end.setHours(17)
  return (
    <ActionForm
      label="Создать заявку"
      initial={{
        clientId: data.clients[0]?.id,
        quantity: 1,
        fuelLiters: 0,
        startAt: localDateTime(tomorrow.toISOString()),
        endAt: localDateTime(end.toISOString()),
      }}
      fields={(v) => [
        {
          name: 'clientId',
          label: 'Клиент',
          type: 'select',
          options: data.clients.map((c) => ({ value: c.id, label: c.name })),
        },
        {
          name: 'siteId',
          label: 'Объект клиента',
          type: 'select',
          options: data.sites
            .filter(
              (s) => s.clientId === v.clientId && !['frozen', 'completed', 'archived'].includes(s.status),
            )
            .map((s) => ({ value: s.id, label: s.name })),
        },
        {
          name: 'type',
          label: 'Тип техники',
          type: 'select',
          options: data.equipmentTypes.map((type) => ({ value: type, label: type })),
        },
        { name: 'quantity', label: 'Количество единиц', type: 'number', min: 1, max: 20, step: 1 },
        { name: 'startAt', label: 'Начало (МСК)', type: 'datetime-local' },
        { name: 'endAt', label: 'Окончание (МСК)', type: 'datetime-local' },
        { name: 'fuelLiters', label: 'Резерв дизельного топлива на заявку, л', type: 'number', min: 0 },
        { name: 'comment', label: 'Задача / условия работы', type: 'textarea', required: false, wide: true },
      ]}
      submit={(v, key) =>
        command('request.create', '', { ...v, startAt: toISO(v.startAt), endAt: toISO(v.endAt) }, key)
      }
      onDone={onDone}
    />
  )
}

export function RequestDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const data = useProject()
  const { role } = useAppState()
  const r = data.requests.find((r) => r.id === id)
  const [artifact, setArtifact] = useState<Artifact | null>(null)
  if (!r) return null
  const assignments = data.scheduleAssignments.filter((a) => a.requestId === id)
  return (
    <Modal title={r.number + ' · ' + r.client} onClose={onClose}>
      <div className="request-overview">
        <section>
          <Badge tone={requestStatus[r.status].tone}>{requestStatus[r.status].label}</Badge>
          <h3>
            {r.quantity} × {r.type}
          </h3>
          <p>{r.site}</p>
          <p>
            {dateTime(r.startAt)} — {dateTime(r.endAt)} (МСК)
          </p>
          <p>{r.comment || 'Без дополнительных условий'}</p>
          <p>Резерв топлива: {r.fuelLiters} л</p>
          {!!r.materials?.length && (
            <>
              <h3>Потребность в расходниках</h3>
              {r.materials.map((line) => (
                <p key={line.stockId}>
                  {data.consumables.find((m) => m.id === line.stockId)?.name ?? 'Материал'} · {line.quantity}{' '}
                  {data.consumables.find((m) => m.id === line.stockId)?.unit ?? ''}
                </p>
              ))}
            </>
          )}
        </section>
        <aside>
          <strong>Назначенные ресурсы</strong>
          {assignments.length ? (
            assignments.map((a) => (
              <p key={a.id}>
                {a.equipment}
                <br />
                {a.operator}
                <br />
                {a.status === 'completed'
                  ? 'Смена закрыта'
                  : a.status === 'cancelled'
                    ? 'Назначение отменено'
                    : a.status === 'working'
                      ? 'В работе'
                      : 'Назначен'}
              </p>
            ))
          ) : (
            <p>Ресурсы пока не назначены</p>
          )}
        </aside>
      </div>
      {role !== 'client' && (r.status === 'assigned' || r.status === 'pending') && (
        <ActionForm
          initial={{ status: r.status === 'assigned' ? 'working' : 'cancelled' }}
          fields={[
            {
              name: 'status',
              label: 'Изменить статус',
              type: 'select',
              options:
                r.status === 'assigned'
                  ? [
                      { value: 'working', label: 'Начать работу' },
                      { value: 'cancelled', label: 'Отменить заявку' },
                    ]
                  : [{ value: 'cancelled', label: 'Отменить заявку' }],
            },
          ]}
          submit={(v, key) => command('request.status', r.id, v, key)}
          label="Применить статус"
        />
      )}
      <section className="request-timeline">
        <h3>История заявки</h3>
        {data.events
          .filter((e) => e.entityId === id)
          .map((e) => (
            <div key={e.id}>
              <i className="done" />
              <span>{dateTime(e.createdAt)}</span>
              <strong>
                {(
                  {
                    'request.create': 'Создана заявка',
                    'request.assign': 'Назначены ресурсы',
                    'request.status': 'Изменен статус',
                  } as Record<string, string>
                )[e.action] ?? e.action}
              </strong>
              <small>{e.actor}</small>
            </div>
          ))}
      </section>
      <section className="request-artifacts">
        <h3>Вложения</h3>
        {data.artifacts
          .filter((a) => a.requestId === id)
          .map((a) => (
            <button key={a.id} onClick={() => setArtifact(a)}>
              {a.name}
            </button>
          ))}
        {!data.artifacts.some((a) => a.requestId === id) && (
          <p>Вложений пока нет. Диспетчер может прикрепить файл в разделе «Документы».</p>
        )}
      </section>
      {artifact && (
        <Modal title={artifact.name} onClose={() => setArtifact(null)}>
          <ArtifactViewer artifact={artifact} />
        </Modal>
      )}
    </Modal>
  )
}

export function AssignmentForm({ request, onDone }: { request: EquipmentRequest; onDone: () => void }) {
  const [result, setResult] = useState<Recommendation | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  return (
    <div>
      <p>Подбор проверяет тип, допуск, рабочую смену, запас до ТО и пересечения по времени.</p>
      <button
        className="button secondary"
        disabled={loading}
        onClick={async () => {
          setLoading(true)
          setError('')
          try {
            setResult(await recommend(request.id))
          } catch (e) {
            setError(e instanceof Error ? e.message : 'Ошибка подбора')
          } finally {
            setLoading(false)
          }
        }}
      >
        {loading ? 'Подбор…' : 'Рассчитать доступные комплекты'}
      </button>
      {error && <p role="alert">{error}</p>}
      {result && (
        <>
          <p>
            Доступно комплектов: {result.candidates.length} / требуется {request.quantity}
          </p>
          {result.candidates.slice(0, request.quantity).map((c) => (
            <div className="assignment-result" key={c.equipmentId}>
              <strong>{c.equipment}</strong>
              <p>{c.operator}</p>
              <div className="tag-row">
                {c.reasons.map((reason) => (
                  <span key={reason} className="tag">
                    {reason}
                  </span>
                ))}
              </div>
            </div>
          ))}
          {result.candidates.length >= request.quantity && (
            <ActionForm
              initial={{}}
              fields={[]}
              label="Подтвердить назначение"
              submit={(_, key) =>
                command(
                  'request.assign',
                  request.id,
                  { pairs: result.candidates.slice(0, request.quantity) },
                  key,
                  result.revision,
                )
              }
              onDone={onDone}
            />
          )}
          <details>
            <summary>Почему другие ресурсы исключены</summary>
            {result.excluded.map((e) => (
              <p key={e}>{e}</p>
            ))}
          </details>
        </>
      )}
    </div>
  )
}
